import type { Order, ShippingMethod } from '@commercelayer/sdk'
import { intersect } from '@design-system/utils/array'

export const isHazmat = (method: ShippingMethod) => !!method.metadata?.hazmat
export const isPickUpInStore = (method: ShippingMethod) =>
  !!method.metadata?.pickupinstore

export const getShippingMethods = (order: Order) => {
  const shipments = order.shipments ?? []
  if (!shipments.length) return []

  const availableShippingMethodsPerShipments = shipments.map(
    s => s.available_shipping_methods ?? []
  )

  const availableForAllShippingMethods =
    availableShippingMethodsPerShipments.reduce(
      (availableForAllShippingMethods, shippingMethodsPerShipment) =>
        intersect(
          availableForAllShippingMethods,
          shippingMethodsPerShipment,
          (shippingMethod1, shippingMethod2) =>
            shippingMethod1.id === shippingMethod2.id
        )
    )

  const specificShippingMethods = availableForAllShippingMethods.filter(
    m => !isHazmat(m)
  )
  const specificShippingMethodsWithoutPickUpInStore =
    specificShippingMethods.filter(m => !isPickUpInStore(m))

  const availableShippingMethods =
    specificShippingMethodsWithoutPickUpInStore.length
      ? specificShippingMethods
      : availableForAllShippingMethods

  return availableShippingMethods
}

export const isShippingMethodFree = (
  order: Order,
  shippingMethod: Pick<
    ShippingMethod,
    'id' | 'free_over_amount_float' | 'use_subtotal'
  >,
  freeFromFlexPromoIds: string[] = []
) => {
  // Shipment cost is the carrier's cost. The customer pays the shipping line
  // item's amount plus its discounts, for every shipment using this method.
  const selectedShipments = (order.shipments ?? []).filter(
    shipment => shipment.shipping_method?.id === shippingMethod.id
  )
  const areSelectedShipmentsFree =
    selectedShipments.length > 0 &&
    selectedShipments.every(shipment => {
      const lineItem = order.line_items?.find(
        line => line.item_type === 'shipments' && line.item?.id === shipment.id
      )
      return (
        typeof lineItem?.total_amount_cents === 'number' &&
        lineItem.total_amount_cents + (lineItem.discount_cents ?? 0) === 0
      )
    })
  if (areSelectedShipmentsFree) return true

  // Check if shipping method is free from flex promo
  if (
    freeFromFlexPromoIds.length > 0 &&
    shippingMethod.id &&
    freeFromFlexPromoIds.includes(shippingMethod.id)
  ) {
    return true
  }
  const isFreeOverAmountSpecified = isNonNullable(
    shippingMethod?.free_over_amount_float
  )
  if (!isFreeOverAmountSpecified) return false

  const cartValueToCompare = shippingMethod.use_subtotal
    ? order.subtotal_amount_float!
    : order.total_amount_with_taxes_float!

  const isCartValueOverFreeAmount =
    cartValueToCompare >= shippingMethod.free_over_amount_float!

  return isCartValueOverFreeAmount
}
